/** @format */

import React, {
  useState,
  useRef,
  useImperativeHandle,
  forwardRef,
  useEffect,
  useMemo,
  useCallback
} from 'react'
import { _$, Drawer } from '@king-design/react'
import { useWindowSize, useDebounce } from 'react-use'
import { EChartsOption } from 'echarts-for-react'
import { NoData, TablePage, EchartReact } from 'src/components'
import { handleUnit, unitFormatBps, unitFormatbps } from 'src/utils'
import { echartTooltipOptions } from 'src/utils/echarts'
interface MonitorDashboardChartProps {
  chartData: any
  interval?: number
  height?: number
  checkedKeys?: string[]
  title?: string
  setCheckedKeys?: (keys: string[]) => void
}

export interface MonitorChartItemAppHandle {
  chartResize: () => void
  getOptions: () => EChartsOption
}

const MonitorDashboardChar = React.memo(
  forwardRef<MonitorChartItemAppHandle, MonitorDashboardChartProps>(
    ({ chartData, interval, height, checkedKeys, setCheckedKeys }, ref) => {
      // 聚合无数据抽屉
      const [nullDrawer, setNullDrawer] = useState<boolean>(false)
      const [nullData, setNullData] = useState<any>({})
      const chartRef = useRef(null)
      const { width: winWidth, height: winHeight } = useWindowSize()
      useDebounce(
        () => {
          chartRef?.current?.chartResize()
        },
        300,
        [winWidth, winHeight, height]
      )

      useImperativeHandle(ref, () => ({
        chartResize: () => {
          const chartInstance = chartRef.current?.getEchartsInstance()
          if (chartInstance) {
            chartInstance.resize()
          }
        },
        getOptions: () => {
          const chartInstance = chartRef.current?.getEchartsInstance()
          if (chartInstance) {
            return chartInstance.getOption()
          }
        }
      }))

      const chartOption = useMemo(() => {
        if (chartData?.series?.length) {
          // 计算单位
          let chartUnit = ''
          const first = chartData.series[0].name.split('===')[1]
          if (unitFormatBps(first || '') || unitFormatbps(first || '')) {
            const bpsUnit = chartData.series.every(item => {
              const titleSplit = item?.name.split('===')[1]
              return titleSplit === first
            })
            chartUnit = bpsUnit ? first : ''
          }
          // 图表配置项
          const options: EChartsOption = {
            xAxis: {
              type: 'category',
              boundaryGap: false,
              data: chartData?.xData || [],
              splitLine: {
                show: true
              }
            },
            series: chartData?.series || [],
            yAxis: {
              name:
                chartData?.interval || interval
                  ? `${_$('周期')}:${interval || chartData.interval}s`
                  : '',
              type: 'value',
              axisLine: {
                show: true
              },
              axisLabel: {
                showMinLabel: true,
                formatter: function (value) {
                  return handleUnit(value, chartUnit, true)
                }
              },
              splitLine: {
                show: true,
                lineStyle: {
                  type: 'dashed',
                  color: '#d8dae2'
                }
              }
            },
            tooltip: {
              ...echartTooltipOptions,
              axisPointer: {
                type: 'cross',
                animation: false,
                label: {
                  formatter: function (params) {
                    return params.axisDimension === 'y'
                      ? handleUnit(params.value, chartUnit, true)
                      : params.value
                  }
                }
              },
              extraCssText: 'z-index:99',
              formatter: function (params) {
                // eslint-disable-next-line max-len
                let content = `<div class='chart-tooltip'><p class="chart-tooltip-title" >${params[0]?.axisValue}</p>`
                params.forEach(item => {
                  const { seriesName, value, marker, data } = item
                  const [name, unit] = seriesName.split('===')
                  const calcValue = handleUnit(value, unit) || '-'
                  content += `<div class="chart-tooltip-item">
                              <div class="chart-tooltip-item-name" >
                                <div class="chart-tooltip-item-name-marker">${marker}</div>
                                <p class="chart-tooltip-item-name-text text-overflow">${name}</p>
                              </div>
                              <p class="chart-tooltip-item-value">${calcValue} ${
                    data?.null_data?.length
                      ? // eslint-disable-next-line max-len
                        `<span class="chart-tooltip-item-null" onclick="openNullDrawer('${
                          params[0]?.axisValue
                        }','${name}','${data?.null_data
                          .map(
                            ({ instanceId, instanceName, tag }) =>
                              `${instanceId}===${instanceName}===${tag}`
                          )
                          .join(',,,')}')">(${data?.null_data.length})</span>`
                      : ''
                  }</p>
                          </div>`
                })
                return content
              }
            },
            legend: {
              type: 'scroll',
              right: '0',
              width: '85%',
              top: '-3px',
              formatter: name => {
                const nameSplit = name.split('===')
                return nameSplit[0]
              }
            },
            grid: {
              left: 25,
              right: 40,
              top: 35,
              bottom: 15,
              containLabel: true
            }
          }
          // legend关联table的checkedKeys
          if (checkedKeys) {
            const keys = {}
            chartData?.series.forEach(({ name }) => {
              keys[name] = checkedKeys?.includes(name) ? true : false
            })
            options.legend.selected = keys
          }
          return options
        }
      }, [checkedKeys, chartData?.series])

      const openNullDrawer = useCallback(
        (time: string, name: string, params: string) => {
          const paramsSplit = params.split(',,,')
          const dataSplit = paramsSplit.map(item => {
            const [instanceId, instanceName, tag] = item.split('===')
            return {
              instanceId,
              instanceName,
              tag
            }
          })
          setNullData({ time, name, data: dataSplit })
          setNullDrawer(true)
        },
        [chartData]
      )

      useEffect(() => {
        window.openNullDrawer = openNullDrawer
        // 清理函数，卸载组件时移除全局函数
        return () => {
          delete (window as any).openNullDrawer
        }
      }, [openNullDrawer])

      const tableScheme = {
        instanceName: {
          title: _$('实例名称')
        },
        instanceId: {
          title: _$('实例ID')
        },
        tag: {
          title: _$('维度'),
          width: 100
        }
      }

      return chartData ? (
        <>
          <EchartReact
            ref={chartRef}
            onEvents={{
              legendselectchanged: params => {
                if (setCheckedKeys) {
                  const { selected } = params || {}
                  if (selected) {
                    const keys = Object.keys(selected).filter(key => selected[key])
                    setCheckedKeys(keys)
                  }
                }
              }
            }}
            style={{ width: '100%', height: '100%' }}
            option={chartOption || {}}
          ></EchartReact>
          <Drawer
            value={nullDrawer}
            onChangeValue={show => setNullDrawer(show)}
            title={nullData?.name}
          >
            <h4 style={{ fontSize: 14 }}>时间点：{nullData?.time}，以下数据为空 </h4>
            <TablePage
              showPage={false}
              tableProps={{
                scheme: tableScheme,
                data: nullData?.data || [],
                checkType: 'none'
              }}
            />
          </Drawer>
        </>
      ) : (
        <NoData />
      )
    }
  )
)

export default MonitorDashboardChar
